import { FC, useMemo, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { graphql, useSubscription } from 'react-relay';
import { subscribe } from '../../utils/subscriptionMessageBus';

// components
import { PdpSharedAuctionSubscriptionErrorLazy } from '../PdpSharedAuctionSubscriptionError/PdpSharedAuctionSubscriptionErrorLazy';

type Props = {
    refetch: () => void;
    endDate?: string | null;
};

type RelayVariables = {
    userId: string;
    itemId: string;
    isTrade: boolean;
    isClient: boolean;
    fetchUser: boolean;
    fetchAuction: boolean;
};

type ReduxStoreType = {
    relayVariables: {
        variables: RelayVariables;
    };
};

const MAX_REFETCHES = 5;

const subscription = graphql`
    subscription PdpSharedAuctionSubscription(
        $input: AuctionItemUpdateSubscriptionInput!
        $isTrade: Boolean
        $userId: String = ""
        $isClient: Boolean!
        $fetchUser: Boolean!
        $fetchAuction: Boolean = false
    ) {
        auctionItemUpdate(input: $input) {
            item {
                ...PdpSharedAuctionInfo_item
            }
        }
    }
`;

const CONNECTION_DOWN_MAX = 1000 * 60 * 1.5;

const shouldAlertImmediatly = (endDate?: string | null): boolean => {
    if (endDate) {
        return new Date(endDate).getTime() - new Date().getTime() <= CONNECTION_DOWN_MAX;
    }
    return false;
};

export const PdpSharedAuctionSubscription: FC<Props> = ({ refetch, endDate }) => {
    const { isClient, userId, isTrade, itemId, fetchUser, fetchAuction } = useSelector<
        ReduxStoreType,
        RelayVariables
    >(relay => relay?.relayVariables?.variables || {});
    const [hasSubscriptionError, setHasSubscriptionError] = useState<boolean>(false);
    const endDateRef = useRef<string | null | undefined>(endDate);
    const refetches = useRef<number>(0);

    useEffect(() => {
        endDateRef.current = endDate;
    }, [endDate]);

    useEffect(() => {
        let errorTimer: ReturnType<typeof setTimeout> | null = null;
        const connected = subscribe('connected', () => {
            if (errorTimer && refetches.current < MAX_REFETCHES) {
                refetch();
                clearTimeout(errorTimer);
                errorTimer = null;
                refetches.current += 1;
            }
        });
        const error = subscribe('error', () => {
            if (!errorTimer) {
                errorTimer = setTimeout(
                    () => {
                        setHasSubscriptionError(true);
                    },
                    shouldAlertImmediatly(endDateRef.current) || refetches.current >= MAX_REFETCHES
                        ? 0
                        : CONNECTION_DOWN_MAX
                );
            }
        });

        return () => {
            if (errorTimer) {
                clearTimeout(errorTimer);
            }
            error.dispose();
            connected.dispose();
        };
    }, [refetch]);

    const subscriptionConfig = useMemo(
        () => ({
            subscription,
            variables: {
                userId,
                isTrade,
                isClient,
                fetchUser,
                fetchAuction,
                input: {
                    itemId,
                },
            },
            onError: () => setHasSubscriptionError(true),
        }),
        [isClient, userId, isTrade, itemId, fetchUser, fetchAuction]
    );

    useSubscription(subscriptionConfig);
    return <PdpSharedAuctionSubscriptionErrorLazy hasSubscriptionError={hasSubscriptionError} />;
};
